import { useCallback } from "react";
import {
  generateThemeCSS,
  DEFAULT_THEME_COLORS,
} from "@/shared/lib/utils/generateThemeCSS";
import { type Theme } from "@/shared/lib/types";
import {
  getFirmData,
  getFirmConfig,
  getFirmsData,
} from "@/shared/lib/utils/firm";
import {
  resolveFirmBranding,
  getValidColor,
} from "@/shared/lib/utils/branding";
import { useUserStore } from "@/shared/store/userStore";

// Colors of the domain's firm (the enterprise record on shared domains).
// The firm record's own columns win over any legacy camelCase meta_data keys.
const getDomainThemeColors = (): Theme => {
  const hostname = window.location.hostname;
  return {
    brandPrimary:
      getFirmConfig(hostname, "primary_color", "") ||
      getFirmConfig(
        hostname,
        "primaryColor",
        DEFAULT_THEME_COLORS.brandPrimary,
      ),
    brandSecondary:
      getFirmConfig(hostname, "secondary_color", "") ||
      getFirmConfig(
        hostname,
        "secondaryColor",
        DEFAULT_THEME_COLORS.brandSecondary,
      ),
  };
};

const useUpdateTheme = (): {
  updateThemeCss: (themeColors: Theme) => void;
  updateThemeForFirm: (firmId: string) => void;
  updateThemeForUser: () => void;
  updateThemeToDefault: () => void;
} => {
  const updateThemeCss = useCallback((themeColors: Theme): void => {
    const newThemeCss = generateThemeCSS(themeColors);
    const themeCss = document.getElementById("theme-css");
    if (themeCss?.innerHTML === newThemeCss) {
      return;
    }
    if (themeCss) {
      themeCss.remove();
    }
    const updatedThemeCss = document.createElement("style");
    updatedThemeCss.id = "theme-css";
    updatedThemeCss.innerHTML = newThemeCss;
    document.head.appendChild(updatedThemeCss);
  }, []);

  const updateThemeForFirm = useCallback(
    (firmId: string): void => {
      const firm = getFirmData(firmId);

      if (firm) {
        if (firm.primary_color && firm.secondary_color) {
          const firmColors = {
            brandPrimary: firm.primary_color,
            brandSecondary: firm.secondary_color,
          };
          updateThemeCss(firmColors);
        }
      } else {
        console.log("No custom theme found for firm", firmId);
        return;
      }
    },
    [updateThemeCss],
  );

  // Logged-in user's firm colors, inheriting parent enterprise → domain →
  // defaults for anything the firm doesn't set. Falls back to plain domain
  // colors when nobody is logged in.
  const updateThemeForUser = useCallback((): void => {
    const user = useUserStore.getState().user;
    const domainColors = getDomainThemeColors();
    const resolved = resolveFirmBranding(user?.firm?.id, getFirmsData(), {
      themeColors: domainColors,
    });
    updateThemeCss({
      brandPrimary: getValidColor(
        user?.firm?.primaryColor,
        resolved.brandPrimary,
      ),
      brandSecondary: getValidColor(
        user?.firm?.secondaryColor,
        resolved.brandSecondary,
      ),
    });
  }, [updateThemeCss]);

  const updateThemeToDefault = useCallback((): void => {
    updateThemeCss(getDomainThemeColors());
  }, [updateThemeCss]);

  return {
    updateThemeCss,
    updateThemeForFirm,
    updateThemeForUser,
    updateThemeToDefault,
  };
};

export default useUpdateTheme;
